import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MapPin, Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LocationSearch({ onLocationSelect, placeholder = "Search location..." }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // Use backend proxy to avoid CORS issues
        const response = await axios.get(`${API}/geocode/search`, {
          params: { q: query }
        });
        setSuggestions(response.data);
        setShowDropdown(true);
      } catch (error) {
        console.error("Location search error:", error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const handleSelect = (location) => {
    const selectedLocation = {
      lat: parseFloat(location.lat),
      lng: parseFloat(location.lon),
      displayName: location.display_name,
      name: location.name || location.display_name.split(",")[0],
    };
    setQuery(selectedLocation.name);
    setShowDropdown(false);
    onLocationSelect(selectedLocation);
  };

  const clearSearch = () => {
    setQuery("");
    setSuggestions([]);
    setShowDropdown(false);
  };

  const formatAddress = (location) => {
    const parts = [];
    const addr = location.address || {};
    
    if (addr.neighbourhood) parts.push(addr.neighbourhood);
    if (addr.suburb) parts.push(addr.suburb);
    if (addr.city || addr.town || addr.village) {
      parts.push(addr.city || addr.town || addr.village);
    }
    if (addr.state) parts.push(addr.state);
    
    return parts.slice(0, 3).join(", ") || location.display_name.split(",").slice(0, 3).join(",");
  };

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid="location-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className="pl-10 pr-10 bg-white/95 backdrop-blur shadow-lg border-0 rounded-full h-12"
          data-testid="location-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" size={18} />
        )}
        {query && !loading && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden z-50">
          {suggestions.map((location, index) => (
            <button
              key={location.place_id || index}
              onClick={() => handleSelect(location)}
              className="w-full px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-50 last:border-0"
              data-testid={`location-suggestion-${index}`}
            >
              <MapPin size={18} className="text-sky-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">
                  {location.name || location.display_name.split(",")[0]}
                </p>
                <p className="text-sm text-slate-500 truncate">
                  {formatAddress(location)}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {showDropdown && query.length >= 3 && !loading && suggestions.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-100 p-4 text-center z-50">
          <p className="text-slate-500 text-sm">No locations found for "{query}"</p>
        </div>
      )}
    </div>
  );
}
